import type { Theme } from '@mui/material';
import type { SxStyles } from '@/ui/style/types';
import { visuallyHidden, geoViewColors as defaultGeoViewColors } from '@/ui/style/default';
import { getFocusIndicatorStyles } from '@/ui/style/themeOptionsGenerator';

/**
 * Gets custom sx classes for the slider component.
 *
 * Uses optional chaining (?.) for theme.palette.geoViewColor properties
 * because plugins may render before GeoView's custom theme is fully initialized.
 * Standard MUI properties (theme.palette.common) do not require this.
 *
 * @param theme - The MUI theme object
 * @returns The sx classes object
 */
export const getSxClasses = (theme: Theme): SxStyles => ({
  slider: {
    '& .MuiSlider-thumb': {
      width: 15,
      height: 15,
      color: theme.palette.primary.main,
      transition: '0.3s cubic-bezier(.47,1.64,.41,.8)',
      '&:before': {
        boxShadow: '0 2px 12px 0 rgba(0,0,0,0.4)',
      },
      '&:hover:not(.Mui-focusVisible)': {
        boxShadow: `0px 0px 0px 8px ${'rgb(255 255 255 / 16%)'}`,
      },
      ...(!theme.focusVisible && {
        '&.Mui-focusVisible': getFocusIndicatorStyles(theme.palette.geoViewColor ?? defaultGeoViewColors),
      }),
      ...(theme.focusVisible && {
        // MUI's hover/active variants otherwise replace the theme's keyboard halo.
        '&.Mui-focusVisible:hover, &.Mui-focusVisible.Mui-active': {
          boxShadow: theme.focusVisible.boxShadow,
        },
      }),
      '&.Mui-active': {
        width: 30,
        height: 30,
      },
    },
    '& .MuiSlider-thumb:hover:not(.Mui-focusVisible)': {
      boxShadow: 'rgba(1, 0, 155, 0.7) 0px 0px 0px 3px !important',
    },
    '& .MuiSlider-valueLabel': {
      fontSize: theme.typography.caption.fontSize,
      padding: theme.spacing(0.5, 0.75),
      [theme.breakpoints.down('md')]: {
        ...visuallyHidden, // Hide value tooltips on tablets and below
      },
    },
    '&.MuiSlider-labelSpread .MuiSlider-thumb:nth-last-of-type(2) .MuiSlider-valueLabel.MuiSlider-valueLabelOpen': {
      transform: 'translateX(-42%) translateY(-100%) scale(1)',
      '&:before': {
        left: 'calc(100% - 6px)',
      },
    },
    '&.MuiSlider-labelSpread .MuiSlider-thumb:last-of-type .MuiSlider-valueLabel.MuiSlider-valueLabelOpen': {
      transform: 'translateX(42%) translateY(-100%) scale(1)',
      '&:before': {
        left: '6px',
      },
    },
    '& .MuiSlider-rail': {
      opacity: 0.35,
      color: theme.palette.geoViewColor?.grey.darken(0.9, 0.87),
    },
    '& .MuiSlider-track': {
      color: theme.palette.primary.main,
    },
    '& .MuiSlider-mark': {
      height: 4,
      width: 4,
      color: theme.palette.common.black,
    },
    '& .MuiSlider-markLabel': {
      color: theme.palette.text.secondary,
      '&.MuiSlider-markLabelActive': {
        color: theme.palette.text.primary,
      },
    },
    '& .MuiSlider-markLabel-overlap': {
      display: 'none !important',
    },
    '& .MuiSlider-mark-hidden': {
      opacity: 0,
      pointerEvents: 'none',
    },
  },
});
